import CloseIcon from '@mui/icons-material/Close';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import MenuIcon from '@mui/icons-material/Menu';
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  IconButton,
  useMediaQuery,
  useTheme,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router';
import { useThemeMode } from '@/contexts/ThemeModeContext';
import { LanguageSelector } from '../ui/LanguageSelector';

const navLinks = [
  { labelKey: 'nav.home', path: '/' },
  { labelKey: 'nav.about', path: '/about' },
  { labelKey: 'nav.skills', path: '/skills' },
  { labelKey: 'nav.projects', path: '/projects' },
  { labelKey: 'nav.contact', path: '/contact' },
];

export const Navbar = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { mode, toggleTheme } = useThemeMode();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false);
  };

  return (
    <>
      {/* Skip to main content link for accessibility */}
      <Box
        component="a"
        href="#main-content"
        sx={{
          position: 'absolute',
          left: '-9999px',
          zIndex: 9999,
          bgcolor: 'background.paper',
          color: 'text.primary',
          p: 2,
          borderRadius: 1,
          textDecoration: 'none',
          fontWeight: 600,
          '&:focus': {
            left: '50%',
            transform: 'translateX(-50%)',
            top: '10px',
            boxShadow: 3,
          },
        }}
      >
        Skip to main content
      </Box>

      <AppBar
        component="nav"
        aria-label="Main navigation"
        position="fixed"
        elevation={0}
        sx={{
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid',
          borderColor: theme.palette.divider,
        }}
      >
        <Toolbar
          sx={{
            maxWidth: '1280px',
            width: '100%',
            mx: 'auto',
            px: { xs: 2, sm: 3, lg: 4 },
          }}
        >
          {/* Logo */}
          <Box
            component={Link}
            to="/"
            aria-label="Home"
            sx={{
              display: 'flex',
              alignItems: 'center',
              textDecoration: 'none',
              fontFamily: '"Space Grotesk", sans-serif',
              fontWeight: 700,
              fontSize: '1.5rem',
              color: 'text.primary',
              letterSpacing: '-0.02em',
              transition: 'color 0.3s',
              '&:hover': {
                color: 'primary.main',
              },
            }}
          >
            SN
            <Box component="span" sx={{ color: 'primary.main' }}>
              .
            </Box>
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          {/* Desktop Navigation */}
          {!isMobile && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {navLinks.map((link) => {
                const isActive = location.pathname === link.path;
                return (
                  <Button
                    key={link.path}
                    component={Link}
                    to={link.path}
                    sx={{
                      color: isActive ? 'primary.main' : 'text.secondary',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      px: 2,
                      py: 1,
                      position: 'relative',
                      '&:hover': {
                        color: 'text.primary',
                        bgcolor: 'action.hover',
                      },
                      ...(isActive && {
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          bottom: 0,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          width: '100%',
                          height: '2px',
                          bgcolor: 'primary.main',
                        },
                      }),
                    }}
                  >
                    {t(link.labelKey)}
                  </Button>
                );
              })}

              {/* Divider */}
              <Box
                sx={{
                  width: '1px',
                  height: '24px',
                  bgcolor: theme.palette.divider,
                  mx: 1,
                }}
              />

              {/* Theme Toggle */}
              <IconButton
                onClick={toggleTheme}
                aria-label={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                sx={{
                  color: 'text.secondary',
                  transition: 'all 0.3s',
                  '&:hover': {
                    color: 'primary.main',
                    bgcolor: 'action.hover',
                    transform: 'rotate(180deg)',
                  },
                }}
              >
                {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
              </IconButton>

              {/* Divider */}
              <Box
                sx={{
                  width: '1px',
                  height: '24px',
                  bgcolor: theme.palette.divider,
                  mx: 1,
                }}
              />

              {/* Language Selector */}
              <LanguageSelector />
            </Box>
          )}

          {/* Mobile Menu Button */}
          {isMobile && (
            <>
              {/* Theme Toggle for Mobile */}
              <IconButton
                onClick={toggleTheme}
                aria-label={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                sx={{
                  color: 'text.secondary',
                  mr: 1,
                  '&:hover': {
                    color: 'primary.main',
                  },
                }}
              >
                {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
              </IconButton>

              <IconButton
                edge="end"
                color="inherit"
                aria-label="menu"
                onClick={handleMobileMenuToggle}
                sx={{ color: 'text.primary' }}
              >
                <MenuIcon />
              </IconButton>
            </>
          )}
        </Toolbar>

        {/* Mobile Drawer */}
        <Drawer
          anchor="right"
          open={mobileMenuOpen}
          onClose={handleMobileMenuClose}
          slotProps={{
            paper: {
              sx: {
                width: '280px',
                bgcolor: 'background.paper',
                backdropFilter: 'blur(12px)',
                borderLeft: '1px solid',
                borderColor: theme.palette.divider,
              },
            },
          }}
        >
          {/* Drawer Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              borderBottom: '1px solid',
              borderColor: theme.palette.divider,
            }}
          >
            <Box
              sx={{
                fontFamily: '"Space Grotesk", sans-serif',
                fontWeight: 700,
                fontSize: '1.25rem',
                color: 'text.primary',
              }}
            >
              SN
              <Box component="span" sx={{ color: 'primary.main' }}>
                .
              </Box>
            </Box>
            <IconButton onClick={handleMobileMenuClose} sx={{ color: 'text.secondary' }}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Navigation Links */}
          <List sx={{ px: 1, py: 2 }}>
            {navLinks.map((link) => {
              const isActive = location.pathname === link.path;
              return (
                <ListItem key={link.path} disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    component={Link}
                    to={link.path}
                    onClick={handleMobileMenuClose}
                    sx={{
                      borderRadius: '8px',
                      py: 1.5,
                      px: 2,
                      color: isActive ? 'primary.main' : 'text.secondary',
                      bgcolor: isActive ? 'action.selected' : 'transparent',
                      '&:hover': {
                        bgcolor: 'action.hover',
                        color: 'text.primary',
                      },
                    }}
                  >
                    <ListItemText
                      primary={t(link.labelKey)}
                      slotProps={{
                        primary: {
                          sx: {
                            fontWeight: isActive ? 600 : 500,
                            fontSize: '0.95rem',
                          },
                        },
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>

          <Divider sx={{ mx: 2 }} />

          {/* Theme Toggle and Language Selector */}
          <Box sx={{ p: 2 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 2,
              }}
            >
              <Box sx={{ color: 'text.secondary', fontSize: '0.875rem', fontWeight: 500 }}>
                {t('Theme')}
              </Box>
              <IconButton
                onClick={toggleTheme}
                size="small"
                sx={{
                  color: 'text.secondary',
                  '&:hover': {
                    color: 'primary.main',
                  },
                }}
              >
                {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
              </IconButton>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ color: 'text.secondary', fontSize: '0.875rem', fontWeight: 500 }}>
                {t('Language')}
              </Box>
              <LanguageSelector />
            </Box>
          </Box>
        </Drawer>
      </AppBar>
    </>
  );
};
